export interface DesktopAgentPairingState {
    isPaired: boolean;
    deviceIdentifier: string;
    registrationId?: string | null;
    deviceName: string;
    apiBaseUrl?: string | null;
    pairedAtUtc?: string | null;
    lastHeartbeatAtUtc?: string | null;
}

export interface DesktopAgentLifecycle {
    trayAvailable: boolean;
    autoStartSupported: boolean;
    autoStartEnabled: boolean;
    pairing: DesktopAgentPairingState;
}

class DesktopAgentLifecycleService {
    async lifecycle(port: number): Promise<DesktopAgentLifecycle> {
        const response = await this.fetchLocal(port, '/lifecycle');
        return await response.json() as DesktopAgentLifecycle;
    }

    async setAutoStart(port: number, enabled: boolean): Promise<void> {
        await this.fetchLocal(port, '/settings/autostart', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ enabled }),
        });
    }

    async pair(port: number, apiBaseUrl: string, code: string): Promise<DesktopAgentPairingState> {
        const response = await this.fetchLocal(port, '/pair', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ apiBaseUrl, code }),
        }, 30_000);
        return await response.json() as DesktopAgentPairingState;
    }

    async unpair(port: number): Promise<void> {
        await this.fetchLocal(port, '/pair', { method: 'DELETE' }, 20_000);
    }

    private async fetchLocal(
        port: number,
        path: string,
        init?: RequestInit,
        timeoutMs = 5_000,
    ): Promise<Response> {
        const controller = new AbortController();
        const timeout = window.setTimeout(() => controller.abort(), timeoutMs);
        try {
            const response = await fetch(`http://127.0.0.1:${port}${path}`, {
                ...init,
                signal: controller.signal,
                cache: 'no-store',
            });
            if (!response.ok) {
                const payload = await response.json().catch(() => null) as
                    { error?: string; detail?: string; title?: string } | null;
                throw new Error(payload?.error || payload?.detail || payload?.title || `Desktop Agent: errore ${response.status}.`);
            }
            return response;
        } catch (error) {
            if (error instanceof DOMException && error.name === 'AbortError')
                throw new Error('IA Desktop Agent non raggiungibile o richiesta scaduta.');
            throw error;
        } finally {
            window.clearTimeout(timeout);
        }
    }
}

export default new DesktopAgentLifecycleService();
